// ClassroomPage.js - with URL sync
import { observer } from 'mobx-react-lite';
import urls from '../../Mobx/Urls';
import Layout from '../Layout/Layout';
import { useEffect, useState, useCallback} from 'react';
import {
    Box, Typography, Snackbar, Alert, CircularProgress
} from '@mui/material';
import axios from 'axios';
import UsersFilterPanel from './UsersFilterPanel';
import ClassroomUsersTable from './ClassroomUsersTable';
import UpdateUser from '../Customers/UpdateUser.js';
import { useLocation } from 'react-router-dom';
import { parseQueryParams, updateUrlParams } from '../../Utils/urlUtils';

const ClassroomPage = observer(() => {
    const location = useLocation();
    const VISIBLE_FIELDS_KEY = 'visibleFieldsClassroom';

    const {
        filters: initialFilters,
        sortField: initialSortField,
        sortOrder: initialSortOrder,
        page: initialPage,
        limit: initialLimit
    } = parseQueryParams(
        new URLSearchParams(location.search),
        [
            'firstName', 'lastName', 'nickname', 'email',
            'role', 'subscriptionLevel', 'typeOfDictionary',
            'learningLanguage', 'city', 'school', 'class',
            'parentalControl', 'country', 'serial', 'archive',
            'permissionProfile','goalId'
        ],
        'createdAt'
    );

    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(initialPage);
    const [filters, setFilters] = useState(initialFilters);
    const [sortField, setSortField] = useState(initialSortField);
    const [sortOrder, setSortOrder] = useState(initialSortOrder);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [url, setUrl] = useState(urls.user.getGoalWithPagination);

    const [visibleFields, setVisibleFields] = useState(() => {
        const stored = localStorage.getItem(VISIBLE_FIELDS_KEY);
        return stored ? JSON.parse(stored) : [
            'firstName', 'lastName', 'email', 'password', 'role',
            'createdAt', 'subscriptionLevel', 'profilePicture',
            'serial'
        ];
    });

    // ---------------
    const LIMIT_KEY = 'limitClassroomPage';
    const [limit, setLimit] = useState(() => {
        const storedLimit = localStorage.getItem(LIMIT_KEY);
        return storedLimit ? Number(storedLimit) : initialLimit;
    });
    useEffect(() => {
        localStorage.setItem(LIMIT_KEY, limit);
    }, [limit]);
    // ---------------

    useEffect(() => {
        localStorage.setItem(VISIBLE_FIELDS_KEY, JSON.stringify(visibleFields));
    }, [visibleFields]);

    const fetchUsers = useCallback(async () => {
        try {
            let _url = url;
            if (filters.goalId) {
                _url = urls.user.getGoalUserWithPagination;
            } else if (filters.serial){
                _url = urls.user.getAllWithPagination;
            } else {
                _url = urls.user.getGoalWithPagination;
            }
            setUrl(_url);
            setIsLoading(true);
            
            const response = await axios.post(_url, {
                page,
                limit,
                filters: filters,
                sort: [{ field: sortField, order: sortOrder }]
            }, { withCredentials: true });

            setUsers(response.data.users);
            setTotal(response.data.total);
        // });
        } catch (error) {
            console.error('Failed to fetch users:', error);
            setErrorMessage(`שגיאה בטעינת המשתמשים: ${error?.response?.data}.`);
            setIsSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    }, [page, limit, filters, sortField, sortOrder, url]);

    const handleArchive = async (user, archiveValue) => {
        try {
            await axios.patch(urls.user.update, { _id: user._id, archive: archiveValue }, { withCredentials: true });
            fetchUsers();
        } catch (error) {
            setErrorMessage(`שגיאה בעדכון מצב הארכיון של המשתמש: ${error}. ${error?.response?.data}.`);
            setIsSnackbarOpen(true);
        }
    };

    useEffect(() => {
        updateUrlParams({ filters, sortField, sortOrder, page, limit, dateKey: 'createdAt' });
        fetchUsers();
    }, [filters, sortField, sortOrder, page, limit, fetchUsers]);

    const handleCloseSnackbar = () => setIsSnackbarOpen(false);

    const tools = [
        <Box
            key="total-users"
            sx={{
                backgroundColor: 'white',
                px: 2,
                py: 1,
                borderRadius: 2,
                boxShadow: 1,
                display: 'inline-block',
                fontWeight: 'bold',
                color: 'primary.main',
                border: '1px solid #ccc',
                minWidth: '220px', textAlign: 'center'
            }}
        >
            <Typography variant="body2">
                מספר משתמשים שנמצאו: {total}
            </Typography>
        </Box>
    ];

    const drawerTools = [
        <UsersFilterPanel
            key="filter-panel"
            filters={filters}
            setFilters={setFilters}
            sortField={sortField}
            setSortField={setSortField}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            fetchUsers={fetchUsers}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
            visibleFields={visibleFields}
            setVisibleFields={setVisibleFields}
        />
    ];

    return (
        <div className="ClassroomPage">
            <Layout tools={tools} drawerTools={drawerTools} toolsConfig={{ toolsHeight: 45, toolsBackgroundColor: '#FFFFFF', drawerToolsWidth: 410 }}>
                {isLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: `calc(100vh - 95px)`
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            p: { xs: 0, md: 0 },
                            height: `calc(100vh - 95px)`,
                            overflowY: { xs: 'visible', md: 'auto' },
                            flexGrow: 1,
                        }}
                    >
                        <ClassroomUsersTable
                            users={users}
                            total={total}
                            page={page}
                            limit={limit}
                            setPage={setPage}
                            onEdit={setEditUser}
                            onArchive={handleArchive}
                            visibleFields={visibleFields}
                            setFilters={setFilters}
                        />
                    </Box>
                )}
            </Layout>

            <UpdateUser
                user={editUser}
                onClose={() => setEditUser(null)}
                onSuccess={fetchUsers}
            />

            <Snackbar
                open={isSnackbarOpen}
                autoHideDuration={null}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </div>
    );
});

export default ClassroomPage;